<template>
  <div id="app">
    <div v-if="showInstallPrompt" class="pwa-install-prompt">
      <span>Adicione o EasySaude à tela inicial para uma melhor experiência!</span>
      <button @click="installPWA">Adicionar</button>
      <button @click="showInstallPrompt = false">Fechar</button>
    </div>

    <transition name="pwa-update-slide">
      <div v-if="showUpdateAvailable" class="pwa-update-banner">
        <div class="pwa-update-content">
          <div class="pwa-update-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="23 4 23 10 17 10" />
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </svg>
          </div>
          <span class="pwa-update-text">Uma nova versão está disponível!</span>
          <button class="pwa-update-btn" @click="applyUpdate">Atualizar agora</button>
          <button class="pwa-update-dismiss" @click="showUpdateAvailable = false" aria-label="Fechar">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>
    </transition>

    <router-view />
  </div>
</template>

<script>
import { SW_UPDATE_EVENT } from './registerServiceWorker';

export default {
  name: "app",
  data() {
    return {
      showInstallPrompt: false,
      deferredPrompt: null,
      showUpdateAvailable: false,
      swRegistration: null,
    };
  },
  page: {
    titleTemplate(title) {
      return process.env.VUE_APP_NAME ? process.env.VUE_APP_NAME : title;
    }
  },
  created() {
    document.addEventListener(SW_UPDATE_EVENT, this.onSwUpdated, { once: true });
  },
  mounted() {
    window.addEventListener("beforeinstallprompt", (e) => {
      e.preventDefault();
      this.deferredPrompt = e;
      this.showInstallPrompt = true;
    });
  },
  beforeDestroy() {
    document.removeEventListener(SW_UPDATE_EVENT, this.onSwUpdated);
  },
  methods: {
    onSwUpdated(event) {
      this.swRegistration = event.detail;
      this.showUpdateAvailable = true;
    },

    applyUpdate() {
      this.showUpdateAvailable = false;

      if (!this.swRegistration || !this.swRegistration.waiting) {
        window.location.reload();
        return;
      }

      this.swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });

      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload();
      });
    },

    installPWA() {
      if (this.deferredPrompt) {
        this.deferredPrompt.prompt();
        this.deferredPrompt.userChoice.then(() => {
          this.showInstallPrompt = false;
          this.deferredPrompt = null;
        });
      }
    },
  },
};
</script>

<style>
.pwa-install-prompt {
  position: fixed;
  bottom: 24px;
  left: 24px;
  background: #4DBA87;
  color: #fff;
  padding: 16px 24px;
  border-radius: 8px;
  z-index: 9999;
  display: flex;
  align-items: center;
  gap: 12px;
}
.pwa-install-prompt button {
  background: #fff;
  color: #4DBA87;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
}

/* Banner de atualização PWA */
.pwa-update-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10000;
  background: linear-gradient(135deg, #1a73e8 0%, #1557b0 100%);
  color: #fff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
}

.pwa-update-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px 20px;
  max-width: 960px;
  margin: 0 auto;
}

.pwa-update-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  animation: pwa-spin 2s linear infinite;
}

.pwa-update-text {
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
}

.pwa-update-btn {
  flex-shrink: 0;
  background: #fff;
  color: #1a73e8;
  border: none;
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;
}

.pwa-update-btn:hover {
  background: #e8f0fe;
}

.pwa-update-btn:active {
  transform: scale(0.97);
}

.pwa-update-dismiss {
  flex-shrink: 0;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  border-radius: 50%;
  transition: color 0.2s, background 0.2s;
}

.pwa-update-dismiss:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.15);
}

/* Animação de entrada/saída */
.pwa-update-slide-enter-active {
  transition: transform 0.4s ease, opacity 0.4s ease;
}

.pwa-update-slide-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.pwa-update-slide-enter,
.pwa-update-slide-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

@keyframes pwa-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@media (max-width: 600px) {
  .pwa-update-content {
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
    padding: 10px 16px;
  }

  .pwa-update-text {
    white-space: normal;
    text-align: center;
    flex-basis: 100%;
    order: 1;
  }

  .pwa-update-icon {
    order: 0;
  }

  .pwa-update-btn {
    order: 2;
  }

  .pwa-update-dismiss {
    position: absolute;
    top: 8px;
    right: 8px;
    order: 3;
  }

  .pwa-update-banner {
    position: fixed;
  }
}
</style>